import React, { useCallback, useEffect, useState } from 'react'
import { Bell, Pencil, Trash2, BarChart2, Plus, X } from 'lucide-react'
import { notificationsAPI, platformAPI, subscriptionTiersAPI } from '../services/api'
import { useAuth } from '../contexts/AuthContext'
import { NotificationEditor } from '../components/NotificationEditor'

const statusBadge = (status) => {
  const map = {
    active: 'bg-emerald-100 text-emerald-800',
    scheduled: 'bg-amber-100 text-amber-800',
    expired: 'bg-gray-200 text-gray-700',
    unpublished: 'bg-slate-200 text-slate-700',
    deleted: 'bg-red-100 text-red-800',
  }
  return map[status] || 'bg-gray-100 text-gray-800'
}

const STATUS_LABEL = {
  all: 'Todas',
  active: 'Ativa',
  scheduled: 'Agendada',
  expired: 'Expirada',
  unpublished: 'Não publicada',
  deleted: 'Excluída',
}

const AUDIENCE_LABEL = {
  all_tenants: 'Todos os estúdios',
  specific_tenants: 'Estúdios específicos',
  subscription_tiers: 'Planos de assinatura',
}

export const NotificationsPage = () => {
  const { isOwner } = useAuth()
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [statusFilter, setStatusFilter] = useState('all')
  const [tenants, setTenants] = useState([])
  const [tiers, setTiers] = useState([])
  const [modal, setModal] = useState(null) // 'create' | { edit: id } | null
  const [editing, setEditing] = useState(null)
  const [saving, setSaving] = useState(false)
  const [stats, setStats] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await notificationsAPI.list({
        status: statusFilter,
        per_page: 50,
        ...(statusFilter === 'deleted' ? { include_deleted: 'true' } : {}),
      })
      setRows(res.data.data || [])
    } catch (err) {
      setError(err.response?.data?.error?.message || err.message || 'Falha ao carregar')
    } finally {
      setLoading(false)
    }
  }, [statusFilter])

  useEffect(() => {
    load()
  }, [load])

  useEffect(() => {
    let canceled = false
    const boot = async () => {
      try {
        const [tr, tn] = await Promise.all([
          subscriptionTiersAPI.getAll({ include_inactive: 'true' }),
          platformAPI.getTenants({ per_page: 200 }),
        ])
        if (canceled) return
        setTiers(tr.data?.data || [])
        setTenants(tn.data?.data || tn.data?.tenants || [])
      } catch {
        /* optional */
      }
    }
    boot()
    return () => {
      canceled = true
    }
  }, [])

  const openCreate = () => {
    setEditing(null)
    setModal('create')
  }

  const openEdit = async (id) => {
    try {
      const res = await notificationsAPI.getOne(id)
      setEditing(res.data.data)
      setModal({ edit: id })
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Falha ao carregar a notificação')
    }
  }

  const openStats = async (id) => {
    try {
      const res = await notificationsAPI.stats(id)
      setStats(res.data.data)
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Falha ao carregar as estatísticas')
    }
  }

  const closeStats = () => setStats(null)

  const handleSave = async (payload) => {
    setSaving(true)
    setError(null)
    try {
      if (modal === 'create') {
        await notificationsAPI.create(payload)
      } else if (modal?.edit) {
        await notificationsAPI.update(modal.edit, payload)
      }
      setModal(null)
      setEditing(null)
      await load()
    } catch (err) {
      setError(err.response?.data?.error?.message || err.message || 'Falha ao salvar')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (id) => {
    if (!window.confirm('Excluir esta notificação?')) return
    try {
      await notificationsAPI.remove(id)
      await load()
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Falha ao excluir')
    }
  }

  const togglePublish = async (row) => {
    try {
      await notificationsAPI.update(row.id, { is_published: !row.is_published })
      await load()
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Falha ao atualizar')
    }
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <Bell className="h-8 w-8 text-primary-600" />
          <div>
            <h1 className="text-2xl font-bold text-admin-900">Notificações</h1>
            <p className="text-admin-600 text-sm">
              Envie comunicados aos administradores dos estúdios (markdown, links, YouTube).
            </p>
          </div>
        </div>
        {isOwner && (
        <button type="button" className="btn btn-primary inline-flex items-center gap-2" onClick={openCreate}>
          <Plus className="h-4 w-4" />
          Nova notificação
        </button>
        )}
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      )}

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <span className="text-sm text-admin-600">Status:</span>
        {['all', 'active', 'scheduled', 'expired', 'unpublished', 'deleted'].map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setStatusFilter(s)}
            className={`rounded-full px-3 py-1 text-xs font-medium border ${
              statusFilter === s
                ? 'bg-primary-600 text-white border-primary-600'
                : 'bg-white text-admin-700 border-admin-200 hover:bg-admin-50'
            }`}
          >
            {STATUS_LABEL[s]}
          </button>
        ))}
      </div>

      <div className="card overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-admin-600">Carregando…</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-admin-200">
              <thead className="bg-admin-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-admin-600 uppercase">
                    Título
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-admin-600 uppercase">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-admin-600 uppercase">
                    Público
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-admin-600 uppercase">
                    Publicação
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-admin-600 uppercase">
                    % de leitura
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-admin-600 uppercase">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-admin-200 bg-white">
                {rows.map((row) => (
                  <tr key={row.id} className="hover:bg-admin-50">
                    <td className="px-4 py-3 text-sm font-medium text-admin-900 max-w-xs truncate">
                      {row.title}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${statusBadge(
                          row.status
                        )}`}
                      >
                        {STATUS_LABEL[row.status] || row.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-admin-700">{AUDIENCE_LABEL[row.audience_type] || row.audience_type}</td>
                    <td className="px-4 py-3 text-xs text-admin-600 whitespace-nowrap">
                      {row.publish_at ? new Date(row.publish_at).toLocaleString('pt-BR') : '—'}
                    </td>
                    <td className="px-4 py-3 text-sm text-admin-800">{row.read_pct ?? 0}%</td>
                    <td className="px-4 py-3 text-right space-x-1">
                      <button
                        type="button"
                        className="p-1.5 rounded hover:bg-admin-100 text-admin-600"
                        title="Estatísticas"
                        onClick={() => openStats(row.id)}
                      >
                        <BarChart2 className="h-4 w-4" />
                      </button>
                      {isOwner && (
                      <button
                        type="button"
                        className="p-1.5 rounded hover:bg-admin-100 text-admin-600"
                        title="Editar"
                        onClick={() => openEdit(row.id)}
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      )}
                      {isOwner && (
                      <button
                        type="button"
                        className="p-1.5 rounded hover:bg-admin-100 text-admin-600"
                        title={row.is_published ? 'Despublicar' : 'Publicar'}
                        onClick={() => togglePublish(row)}
                      >
                        {row.is_published ? 'Desligar' : 'Ligar'}
                      </button>
                      )}
                      {isOwner && (
                      <button
                        type="button"
                        className="p-1.5 rounded hover:bg-red-50 text-red-600"
                        title="Excluir"
                        onClick={() => handleDelete(row.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!rows.length && (
              <div className="p-8 text-center text-admin-500 text-sm">Nenhuma notificação ainda.</div>
            )}
          </div>
        )}
      </div>

      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-xl max-w-5xl w-full max-h-[95vh] overflow-y-auto p-6 relative">
            <button
              type="button"
              className="absolute top-4 right-4 p-2 rounded-lg hover:bg-admin-100"
              aria-label="Fechar"
              onClick={() => {
                if (!saving) {
                  setModal(null)
                  setEditing(null)
                }
              }}
            >
              <X className="h-5 w-5" />
            </button>
            <h2 className="text-xl font-bold text-admin-900 mb-4 pr-10">
              {modal === 'create' ? 'Criar notificação' : 'Editar notificação'}
            </h2>
            <NotificationEditor
              initial={modal === 'create' ? null : editing}
              tenants={tenants}
              tiers={tiers}
              saving={saving}
              onCancel={() => {
                if (!saving) {
                  setModal(null)
                  setEditing(null)
                }
              }}
              onSubmit={handleSave}
            />
          </div>
        </div>
      )}

      {stats && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6 relative">
            <button
              type="button"
              className="absolute top-4 right-4 p-2 rounded-lg hover:bg-admin-100"
              aria-label="Fechar"
              onClick={closeStats}
            >
              <X className="h-5 w-5" />
            </button>
            <h3 className="text-lg font-semibold mb-2">Estatísticas de leitura</h3>
            <p className="text-sm text-admin-600 mb-2">
              Destinatários elegíveis: {stats.eligible_recipient_count ?? '—'}
            </p>
            <p className="text-sm text-admin-600 mb-4">
              OK: {stats.totals?.ok ?? 0} · Ignorados: {stats.totals?.ignore ?? 0}
            </p>
            <div className="max-h-60 overflow-y-auto text-sm border border-admin-200 rounded-lg">
              {(stats.by_tenant || []).map((t) => (
                <div key={t.tenant_id} className="px-3 py-2 border-b border-admin-100 flex justify-between">
                  <span className="font-mono text-xs truncate mr-2">{t.tenant_id}</span>
                  <span>
                    ok {t.ok} / ign. {t.ignore}
                  </span>
                </div>
              ))}
            </div>
            <button type="button" className="btn btn-secondary mt-4" onClick={closeStats}>
              Fechar
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
